import { useState, useEffect, useCallback } from 'react';
import { Box, Typography, CircularProgress, Divider } from '@mui/material';
import { styled } from '@mui/material/styles';
import { FileText, Globe, Database, Cpu, Info } from 'react-feather';
import axios from 'axios';
import logger from '../../../../../utils/logger';

const Container = styled(Box)(({ theme }) => ({
    padding: '24px',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
}));

const Header = styled(Box)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '24px',
}));

const StatBox = styled(Box)(({ theme }) => ({
    backgroundColor: '#f8fafc',
    borderRadius: '8px',
    padding: '16px',
    marginBottom: '12px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
}));

const StatLabel = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    color: '#475569',
    fontSize: '14px',
    fontWeight: 500,
}));

const StatValueContainer = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    textAlign: 'right',
}));

const StatValue = styled(Typography)(({ theme }) => ({
    fontSize: '16px',
    fontWeight: 600,
    color: '#1e293b',
    lineHeight: 1.2,
}));

const SubStat = styled(Typography)(({ theme }) => ({
    fontSize: '12px',
    color: '#94a3b8',
    fontWeight: 400,
    lineHeight: 1.2,
}));

function KnowledgeBaseOverview({ selectedChatbotId, children }) {
    const [stats, setStats] = useState({
        managedFiles: { count: 0, size: 0 },
        websites: { count: 0, size: 0 }, // Websites usually don't have "size" in the same way, but we can track count
        openAIFiles: { count: 0, size: 0 },
        totalSize: 0,
        loading: true,
        hasOpenAI: false
    });

    const formatFileSize = (size) => {
        if (!size) return '0 B';
        if (size <= 1024) return `${size} B`;
        if (size <= 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
        if (size <= 1024 * 1024 * 1024) return `${(size / 1024 / 1024).toFixed(1)} MB`;
        return `${(size / 1024 / 1024 / 1024).toFixed(1)} GB`;
    };

    const fetchStats = useCallback(async () => {
        if (!selectedChatbotId) return;

        try {
            // 1. Fetch Managed Files & Websites (same endpoint)
            const chatbotFilesResponse = await axios.get(`/api/chatbot/${selectedChatbotId}/files`);
            const allFiles = chatbotFilesResponse.data.files || [];

            const managedFiles = allFiles.filter(f => f.source === 'upload' || !f.source || f.source !== 'website');
            const websites = allFiles.filter(f => f.source === 'website');

            // Ensure sizes are parsed as numbers to avoid string concatenation
            const managedSize = managedFiles.reduce((acc, f) => acc + (parseInt(f.size, 10) || 0), 0);

            // 2. Fetch OpenAI Files (if applicable)
            // We'll try to fetch, if it fails (404/400) it likely means no assistant configured or not supported
            let openAIFiles = [];
            let openAISize = 0;
            let hasOpenAI = false;

            try {
                // Check if chatbot has OpenAI config first to avoid unnecessary 400s? 
                // For now, we can just try fetching. The API returns 400 if no assistant ID.
                // const openAIResponse = await axios.get(`/api/openai-assistant/files/${selectedChatbotId}?limit=1`);
                // We actually need the total count/size. The list endpoint might be paginated.
                // The list endpoint returns { files: [], pagination: { total: ... } }
                // But it doesn't give total size easily without fetching all.
                // For overview, we might just show count if size isn't available, or fetch all (might be heavy).
                // Let's assume we can get total from pagination and maybe estimate size or just show count.
                // Actually, let's fetch all for now if the list isn't huge, or just use the first page to check existence.
                // Wait, the user wants "Total Usage total_in_mb". 
                // If the API doesn't support aggregation, we might have to iterate. 
                // For now, let's just use what we have.

                // Optimization: The previous OpenAIFiles component fetched with limit=25.
                // Let's fetch with a larger limit or just use the pagination total.
                // For size, we might be out of luck without fetching all. 
                // Let's try fetching a reasonable amount.
                const openAIFilesResponse = await axios.get(`/api/openai-assistant/files/${selectedChatbotId}?limit=100`);
                openAIFiles = openAIFilesResponse.data.files || [];
                // const totalOpenAICount = openAIFilesResponse.data.pagination?.total || openAIFiles.length; // Unused

                // Calculate size from fetched files (might be partial)
                // Ensure bytes are parsed as numbers to avoid string concatenation
                openAISize = openAIFiles.reduce((acc, f) => acc + (parseInt(f.bytes, 10) || 0), 0);
                hasOpenAI = true;

                // If total > fetched, this size is inaccurate. 
                // Ideally backend should provide stats.
            } catch (err) {
                // Ignore error, assume no OpenAI or not configured
                // logger.warn('Failed to fetch OpenAI stats', err);
            }

            setStats({
                managedFiles: { count: managedFiles.length, size: managedSize },
                websites: { count: websites.length, size: 0 },
                openAIFiles: { count: openAIFiles.length, size: openAISize }, // Note: Count might be partial if > 100
                totalSize: managedSize + openAISize,
                loading: false,
                hasOpenAI
            });

        } catch (error) {
            logger.error('Error fetching knowledge base stats:', error);
            setStats(prev => ({ ...prev, loading: false }));
        }
    }, [selectedChatbotId]);

    useEffect(() => {
        fetchStats();
    }, [fetchStats]);

    if (stats.loading) {
        return (
            <Container>
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', flex: 1 }}>
                    <CircularProgress size={24} />
                </Box>
            </Container>
        );
    }

    return (
        <Container>
            <Header>
                <Typography variant="h6" sx={{ fontSize: '16px', fontWeight: 600 }}>Overview</Typography>
            </Header>

            <StatBox>
                <StatLabel>
                    <FileText size={16} />
                    Managed Files
                </StatLabel>
                <StatValueContainer>
                    <StatValue>{stats.managedFiles.count}</StatValue>
                    <SubStat>{formatFileSize(stats.managedFiles.size)}</SubStat>
                </StatValueContainer>
            </StatBox>

            <StatBox>
                <StatLabel>
                    <Globe size={16} />
                    Websites
                </StatLabel>
                <StatValueContainer>
                    <StatValue>{stats.websites.count}</StatValue>
                    <SubStat>Urls</SubStat>
                </StatValueContainer>
            </StatBox>

            {stats.hasOpenAI && (
                <StatBox>
                    <StatLabel>
                        <Cpu size={16} />
                        OpenAI Files
                    </StatLabel>
                    <StatValueContainer>
                        <StatValue>{stats.openAIFiles.count}</StatValue>
                        <SubStat>{formatFileSize(stats.openAIFiles.size)}</SubStat>
                    </StatValueContainer>
                </StatBox>
            )}

            <Divider sx={{ my: 2 }} />

            <StatBox sx={{ backgroundColor: '#eff6ff', border: '1px solid #dbeafe' }}>
                <StatLabel sx={{ color: '#1e40af' }}>
                    <Database size={16} />
                    Total Usage
                </StatLabel>
                <StatValue sx={{ color: '#1e40af' }}>
                    {formatFileSize(stats.totalSize)}
                </StatValue>
            </StatBox>

            <Box sx={{ mt: 3 }}>
                {children}
            </Box>

            <Box sx={{ mt: 3, p: 2, bgcolor: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                <Typography sx={{ fontSize: '13px', color: '#64748b', display: 'flex', gap: 1 }}>
                    <Info size={16} style={{ minWidth: 16 }} />
                    These resources are used to train your chatbot and improve its responses.
                </Typography>
            </Box>
        </Container>
    );
}

export default KnowledgeBaseOverview;
